/**
 * The organisation's rules for secrets as the console edits them (AIOE-only; the served clause is
 * secrets.ts `secretsPolicySchema`, mirrored with the workbench):
 *
 *   GET    /admin/v1/secrets-policy     { policy: OrgSecretsRules, set }
 *   PUT    /admin/v1/secrets-policy     OrgSecretsRules
 *   DELETE /admin/v1/secrets-policy     back to the defaults
 *   GET    /secrets/v1/scans?projectId= what the workbench's checks of agents' changes found (item C), by project, the
 *                                        last SECRET_SCANS_DAYS days: keepers of the project, and administrators for all
 *
 * Everything in the clause is served to machines in `GET /policy/v1`; `nodeDelegation` is the
 * platform's alone (it decides releases, no machine needs to know it).
 */
import { z } from "zod";
import { secretsPolicySchema } from "./secrets.js";
export const orgSecretsRulesSchema = secretsPolicySchema.extend({
    /**
     * Which secrets a node is given only for people who let it run their work (or an administrator
     * assigned): `personal` (the default) — only a person's own that follow them; `all` — every
     * secret, so a node assigned to a project still cannot ask for a member who never agreed.
     */
    nodeDelegation: z.enum(["personal", "all"]).default("personal"),
});
/**
 * `POST /secrets/v1` and `PUT /secrets/v1/:id/value` refuse a plain value for a person's own secret
 * once that person has registered sealing keys (400 `seal_expected`), unless the body says
 * `plain: true`: their devices seal it before it leaves them.
 */
export const SEAL_EXPECTED_SENTENCE = "Your devices seal your own secrets before they leave them, and this value arrived unsealed: add it from your workbench, or keep it unsealed on purpose.";
// ── Sealed-value formats (D177 follow-up) ──
/*
 * The sealed-value formats this home stores, releases and counts (it never opens one) are the mirrored
 * `SEAL_FORMATS` (secretSeal.ts): `mjuseal1.` and `mjuseal2.` (whose header also names the owner).
 * `GET /secrets/v1/sealing-keys` answers them as `sealFormats`, so a workbench seals in v2 only when
 * the home says it understands it.
 */
/** A sealed value's format by its prefix (`mjuseal2.` → 2, and a later digit as it is); undefined for a plain value. Unlike the mirrored `sealedFormatOf`, it does not stop at the formats this home knows. */
export function sealFormatOf(value) {
    const m = typeof value === "string" ? /^mjuseal(\d)\./.exec(value) : null;
    return m ? Number(m[1]) : undefined;
}
/** Whether a value is sealed, in any format: stored and released as it came, never treated as plain. */
export function isSealedValue(value) {
    return sealFormatOf(value) !== undefined;
}
// ── What the checks of agents' changes found (item C; the workbench's `secrets.scan` rows) ──
export const SECRET_SCANS_DAYS = 30;
//# sourceMappingURL=secretsAdmin.js.map